import {
  CSSProperties,
  ReactNode,
  UIEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'

import {useMobileViewport} from 'hooks/useMobileViewport'
import {useMobileSwipe} from 'hooks/useMobileSwipe'
import {SETTINGS_NAV_PAGES} from 'components/Navigation/SettingsNavigation/settingsNavPages'
import {
  settingsMobileStore,
  useSettingsMobileState,
} from './settingsMobileStore'
import {mobileUnderProps} from 'components/Mobile/mobileUnderProps'
import {MobileBackButton} from 'components/Mobile/MobileBackButton'
import {useMobilePaneFocus} from 'components/Mobile/useMobilePaneFocus'
import './Settings.mobile.css'

// The small page title fades into the bar once the large title scrolls away.
const SCROLLED_TITLE_OFFSET = 40

// A covered page eases 24% left and dims, following the page above it.
const PARALLAX_PERCENT = 24
const COVERED_OPACITY = 0.55

export function coveredStyle(offset: number): CSSProperties {
  const progress = Math.min(1, offset / Math.max(1, window.innerWidth))
  return {
    transform: `translateX(${-PARALLAX_PERCENT * (1 - progress)}%)`,
    opacity: COVERED_OPACITY + (1 - COVERED_OPACITY) * progress,
  }
}

export const SETTINGS_MOBILE_SCROLL_ID = 'settings-mobile-scroll'

interface SettingsMobilePaneContextValue {
  /** Where a second-level page mounts, above this page. */
  subpageSlot: HTMLDivElement | null
  setSubpageOpen: (open: boolean) => void
  setSubpageOffset: (offset: number) => void
}

export const SettingsMobilePaneContext =
  createContext<SettingsMobilePaneContextValue | null>(null)

export function useSettingsMobilePane(): SettingsMobilePaneContextValue | null {
  return useContext(SettingsMobilePaneContext)
}

interface SettingsMobilePageProps {
  children: ReactNode
}

/**
 * On phones and tablets a Settings page slides over the Settings home list,
 * with a back bar, a left-edge swipe strip and swipe/flick/trackpad back.
 * Desktop renders the page untouched.
 */
export function SettingsMobilePage({
  children,
}: Readonly<SettingsMobilePageProps>): JSX.Element {
  const isMobile = useMobileViewport()
  if (!isMobile) return <>{children}</>
  return <SettingsMobilePane>{children}</SettingsMobilePane>
}

function SettingsMobilePane({
  children,
}: Readonly<SettingsMobilePageProps>): JSX.Element {
  const {t} = useTranslation(['settings'])
  const {pathname} = useLocation()
  const {isPageOpen} = useSettingsMobileState()
  const [isSubpageOpen, setSubpageOpen] = useState(false)
  const [subpageOffset, setSubpageOffset] = useState(0)
  const [subpageSlot, setSubpageSlot] = useState<HTMLDivElement | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const backButton = useRef<HTMLButtonElement>(null)
  const page = SETTINGS_NAV_PAGES.find(item => item.pathname === pathname)
  const title = page ? t(page.labelKey) : ''

  const closePage = useCallback(
    () => settingsMobileStore.setPageOpen(false),
    [],
  )
  const {handlers, offset, isDragging} = useMobileSwipe({
    enabled: isPageOpen && !isSubpageOpen,
    direction: 'back',
    onCommit: closePage,
  })

  useEffect(() => {
    settingsMobileStore.setDragOffset(offset)
  }, [offset])

  // Opening from the home list moves focus to the back button.
  useMobilePaneFocus({
    active: isPageOpen,
    targetRef: backButton,
    restoreFocus: false,
    canTakeFocus: focused =>
      !focused ||
      focused === document.body ||
      Boolean(focused.closest('.settings-mobile-home')),
  })

  const onScrollCapture = (event: UIEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement
    // Sideways scrollers (day and filter chips) never move the title.
    if (target.scrollHeight <= target.clientHeight) return
    setIsScrolled(target.scrollTop > SCROLLED_TITLE_OFFSET)
  }

  const paneContext = useMemo(
    () => ({subpageSlot, setSubpageOpen, setSubpageOffset}),
    [subpageSlot],
  )

  return (
    <SettingsMobilePaneContext.Provider value={paneContext}>
      <div
        className="settings-mobile-pane absolute inset-0 overflow-hidden bg-dope-black touch-pan-y"
        data-open={isPageOpen}
        data-dragging={isDragging}
        style={
          isPageOpen && offset
            ? {transform: `translateX(${offset}px)`}
            : undefined
        }
        aria-hidden={!isPageOpen || undefined}
        {...(!isPageOpen ? {inert: ''} : {})}
        {...handlers}
      >
        <div
          className="settings-mobile-pane-content flex flex-col h-full min-h-0"
          {...mobileUnderProps(true, isSubpageOpen)}
          data-dragging={subpageOffset > 0}
          style={
            isSubpageOpen && subpageOffset
              ? coveredStyle(subpageOffset)
              : undefined
          }
        >
          <div
            className={classNames(
              'flex flex-shrink-0 items-center h-[3.25rem] px-1.5 border-b-0.5 transition-colors duration-base ease-standard',
              isScrolled
                ? 'border-gray-1500 bg-dope-black/90'
                : 'border-transparent',
            )}
          >
            <MobileBackButton
              ref={backButton}
              label={t('SETTINGS_TITLE')}
              ariaLabel={t('MOBILE_BACK_TO', {page: t('SETTINGS_TITLE')})}
              onClick={closePage}
              dataCy="settings-mobile-back"
            />
            <span
              className={classNames(
                'flex-1 min-w-0 truncate pr-[5.625rem] text-center abc-light text-type-title text-white transition-opacity duration-base ease-standard',
                isScrolled ? 'opacity-100' : 'opacity-0',
              )}
              aria-hidden={!isScrolled || undefined}
            >
              {title}
            </span>
          </div>
          {/* The left edge always starts a back drag, even over scrollers. */}
          <div
            aria-hidden="true"
            data-cy="settings-mobile-edge"
            className="absolute left-0 top-[3.25rem] bottom-0 z-3 w-6 touch-none"
          />
          <div
            id={SETTINGS_MOBILE_SCROLL_ID}
            className="settings-mobile-scroll flex-1 min-h-0 overflow-y-auto overscroll-contain pb-7"
            onScrollCapture={onScrollCapture}
          >
            {children}
          </div>
        </div>
        <div ref={setSubpageSlot} />
      </div>
    </SettingsMobilePaneContext.Provider>
  )
}
